import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import type { OnboardingScreen, OnboardingStep } from './next-unanswered-step'

export type Section = 'language' | 'company' | 'aboutYou' | 'billing' | 'freeTrial'

const SECTION_OF_STEP: Record<OnboardingScreen, Section> = {
	language: 'language',
	company: 'company',
	organisation_headcount: 'aboutYou',
	manages_team: 'aboutYou',
	contacts_candidates: 'aboutYou',
	recruiting_volume: 'aboutYou',
	phone_number: 'aboutYou',
	billing: 'billing',
	free_trial: 'freeTrial',
}

export function groupIntoSections(steps: readonly OnboardingScreen[]) {
	const sections: { section: Section; steps: OnboardingScreen[] }[] = []
	for (const step of steps) {
		const section = SECTION_OF_STEP[step]
		const last = sections.at(-1)
		if (last?.section === section) {
			last.steps.push(step)
		} else {
			sections.push({ section, steps: [step] })
		}
	}
	return sections
}

export type OnboardingProgressProps = {
	step: OnboardingScreen
	steps: readonly OnboardingScreen[]
	furthestStep: OnboardingStep
	isReachable: (step: OnboardingScreen) => boolean
	onSelect: (step: OnboardingScreen) => void
	disabled: boolean
}

export function OnboardingProgress(
	{ step, steps, furthestStep, isReachable, onSelect, disabled }: OnboardingProgressProps,
) {
	const { t } = useTranslation()
	const furthestIndex = furthestStep === 'complete' ? steps.length : steps.indexOf(furthestStep)
	const isAnswered = (sectionStep: OnboardingScreen) => steps.indexOf(sectionStep) < furthestIndex

	return (
		<nav aria-label={t('onboarding.progress.label')} className="flex-1 min-w-0">
			<ol className="flex gap-2">
				{groupIntoSections(steps).map(({ section, steps: sectionSteps }) => {
					const isCurrent = sectionSteps.includes(step)
					const isDone = !isCurrent && sectionSteps.every(isAnswered)
					const target = isCurrent
						? undefined
						: sectionSteps.find((sectionStep) => sectionStep === furthestStep)
							?? sectionSteps.find(isReachable)
					const label = t(`onboarding.sections.${section}`)

					return (
						<li key={section} className={cn('min-w-0', isCurrent ? 'flex-[2] sm:flex-1' : 'flex-1')}>
							<button
								type="button"
								onClick={() => target && onSelect(target)}
								disabled={disabled || !target}
								aria-current={isCurrent ? 'step' : undefined}
								title={label}
								className="group w-full flex flex-col gap-2 py-1 text-left disabled:cursor-default"
							>
								<span className="flex gap-0.5">
									{sectionSteps.map((sectionStep) => (
										<span
											key={sectionStep}
											className="flex-1 h-1 bg-brand-50 rounded-full overflow-hidden"
										>
											<motion.span
												className="block h-full w-full bg-brand-600 rounded-full origin-left"
												initial={false}
												animate={{
													scaleX: sectionStep === step || isAnswered(sectionStep) ? 1 : 0,
												}}
												transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
											/>
										</span>
									))}
								</span>
								<span
									className={cn(
										'flex items-center gap-1 text-xs truncate',
										isCurrent ? 'font-medium text-foreground' : 'text-muted-foreground',
										!isCurrent && 'hidden sm:flex',
										target && !disabled && 'group-hover:text-foreground',
									)}
								>
									{isDone && <Check aria-hidden className="size-3 shrink-0 text-brand-600" />}
									<span className="truncate">{label}</span>
								</span>
							</button>
						</li>
					)
				})}
			</ol>
		</nav>
	)
}
